import { ChangeDetectionStrategy, Component, inject, output, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { lucideTriangleAlert } from '@ng-icons/lucide';
import { HlmAlertImports } from '@spartan-ng/helm/alert';
import { HlmButtonImports } from '@spartan-ng/helm/button';
import { HlmCardImports } from '@spartan-ng/helm/card';
import { HlmFieldImports } from '@spartan-ng/helm/field';
import { HlmInputImports } from '@spartan-ng/helm/input';
import { HlmSpinnerImports } from '@spartan-ng/helm/spinner';
import { PartnerService } from '../../../../services/partner.service';

/**
 * The step after sign-up: the name the partner appears under. Then the partner area
 * opens with the register link (which also comes by mail). The address is asked for
 * later, together with the bank account, before the first payout.
 */
@Component({
  selector: 'app-partner-profile-step',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgIcon,
    HlmAlertImports,
    HlmButtonImports,
    HlmCardImports,
    HlmFieldImports,
    HlmInputImports,
    HlmSpinnerImports,
  ],
  providers: [provideIcons({ lucideTriangleAlert })],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './partner-profile-step.component.html',
})
export class PartnerProfileStepComponent {
  private readonly partnerService = inject(PartnerService);

  readonly saved = output<void>();

  readonly loading = signal(false);
  readonly submitted = signal(false);
  readonly error = signal<string | null>(null);

  readonly form = inject(FormBuilder).nonNullable.group({
    name: ['', [Validators.required, Validators.minLength(2)]],
  });

  get nameInvalid(): boolean {
    return this.submitted() && this.form.controls.name.invalid;
  }

  submit(): void {
    if (this.loading()) return;
    this.submitted.set(true);
    this.error.set(null);
    if (this.form.invalid) return;

    this.loading.set(true);
    this.partnerService.saveProfile(this.form.getRawValue().name.trim()).subscribe({
      next: () => {
        this.loading.set(false);
        this.saved.emit();
      },
      error: () => {
        this.loading.set(false);
        this.error.set('Das hat nicht geklappt. Bitte versuche es erneut.');
      },
    });
  }
}
